import { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';

/** Spacing options for the Stack component */
type Space = 0 | 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 5 | 6 | 8 | 10 | 12 | 14 | 16 | 20;

/** Alignment options for the Stack component */
type Alignment = 'start' | 'center' | 'end' | 'stretch';

/** Justify options for the Stack component */
type Justify = 'normal' | 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly' | 'stretch';

const alignmentClasses: Record<Alignment, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const justifyClasses: Record<Justify, string> = {
  normal: 'justify-normal',
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
  stretch: 'justify-stretch',
};

const spaceClasses: Record<Space, `gap-${Space}`> = {
  0: 'gap-0', // 0px
  0.5: 'gap-0.5', // 2px
  1: 'gap-1', // 4px
  1.5: 'gap-1.5', // 6px
  2: 'gap-2', // 8px
  2.5: 'gap-2.5', // 10px
  3: 'gap-3', // 12px
  3.5: 'gap-3.5', // 14px
  4: 'gap-4', // 16px
  5: 'gap-5', // 20px
  6: 'gap-6', // 24px
  8: 'gap-8', // 32px
  10: 'gap-10', // 40px
  12: 'gap-12', // 48px
  14: 'gap-14', // 56px
  16: 'gap-16', // 64px
  20: 'gap-20', // 80px
};

/** Props for the Stack component */
type StackProps = HTMLAttributes<HTMLElement | SVGElement> & {
  /** The content to be rendered inside the Stack */
  children: ReactNode;
  /** Additional CSS classes to be applied to the Stack */
  className?: string;
  /** The amount of space between Stack items */
  space?: Space;
  /** The alignment of Stack items along the cross axis */
  align?: Alignment;
  /** The justification of Stack items along the main axis */
  justify?: Justify;
  /**
   * The HTML element to render as.
   * @example
   * ```tsx
   * <Stack as="ul" role="list" aria-label="Features">
   *   <li>Feature 1</li>
   *   <li>Feature 2</li>
   * </Stack>
   * ```
   */
  as?: keyof JSX.IntrinsicElements;
};

/**
 * Stack component for vertical layout with configurable spacing and alignment.
 * Supports all HTML attributes and can be rendered as any HTML or SVG element.
 *
 * @param props - The component props
 * @param props.children - The content to be rendered inside the Stack
 * @param props.className - Additional CSS classes to be applied
 * @param props.space - The amount of space between items (default: 0)
 * @param props.align - The alignment of items along the cross axis (default: 'start')
 * @param props.justify - The justification of items along the main axis (default: 'start')
 * @param props.as - The HTML element to render as (default: 'div')
 * @param props.role - HTML role attribute for accessibility
 * @param props.aria-* - ARIA attributes for accessibility
 * @param props.data-* - Data attributes for custom attributes
 * @returns A flex container with vertical layout
 */
export const Stack = ({
  children,
  className,
  space = 0,
  align = 'start',
  justify = 'start',
  as: Component = 'div',
  ...props
}: StackProps) => {
  return (
    <Component
      className={cn('flex flex-col', spaceClasses[space], alignmentClasses[align], justifyClasses[justify], className)}
      {...props}
    >
      {children}
    </Component>
  );
};
